/**
 * @fileoverview Invitation actions for a user listed in the user directory.
 * @description User Management's row action for a Pending Invitation user: Revoke. The directory
 * (GET /users) lists users, not invitations, so it first finds the user's manageable invitation
 * (findManageableInvitation) and then the Revoke dialog acts on it. Resend goes by the
 * invitation id the directory carries (useResendInvitation), so it needs no lookup.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation } from '@tanstack/react-query'

import { findManageableInvitation } from '@/features/user-invitations/services/user-invitations.service'

import type { InvitationSummary } from '@/features/user-invitations/types/user-invitations.types'
import type { ApiError } from '@/shared/types/http.types'

/** The users feature's GET /users query key prefix (features can't import each other). */
export const USER_DIRECTORY_QUERY_KEY = ['users']

export interface InvitedUser {
  userId: string
  email: string
}

/**
 * @description Finds a user's manageable invitation, e.g. before opening the Revoke dialog.
 * @returns {import('@tanstack/react-query').UseMutationResult<InvitationSummary | null, ApiError, InvitedUser>} The mutation; null when the user has no manageable invitation.
 */
export function useFindUserInvitation() {
  return useMutation<InvitationSummary | null, ApiError, InvitedUser>({
    mutationFn: findManageableInvitation,
  })
}
